import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { SWReactIcons } from '@similarweb/icons';

import { useAppState } from 'AppStateProvider';
import { Dropdown } from 'components/Dropdown';
import { StyledDropdownOption } from 'components/Dropdown/styles';
import type { Contact, EnrichedContactWithCrm, RevealBulkResponse } from 'domain/contact';
import { Crm } from 'domain/crm';
import { useCrmStatusQuery } from 'features/crm/useCrmStatusQuery';
import { BulkContactsExportPreviewModal } from 'pages/contacts/BulkActionBar/BulkContactsExportPreviewModal';
import { useBulkActionsTracking } from 'pages/contacts/BulkActionBar/useBulkActionsTracking';
import { StyledCrmExportBulkButton } from 'pages/contacts/ContactItem/CrmExportContainer/styles';
import { appStore } from 'services/appStore';
import { translate } from 'services/i18n';
import { useUseCases } from 'UseCaseProvider';

import { StyledCrmExportBulkContainer } from './styles';

export const NEW_ACCOUNT_VALUE = 'new_account';

export type CrmExportContainerProps = {
  crm: Crm;
  isDisabled?: boolean;
};

export const BulkCrmExportContainer = ({ crm, isDisabled }: CrmExportContainerProps) => {
  const { selectedContacts, isMasterCheckboxChecked } = useAppState();
  const crmStatusQuery = useCrmStatusQuery(crm);
  const { exportContactsToCrm } = useUseCases();

  const trackingContextRef = useRef<{
    count: number;
    isMaster: boolean;
  } | null>(null);

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [isPreviewModalOpened, setIsPreviewModalOpened] = useState(false);
  const [enrichedContactsWithCRM, setEnrichedContactsWithCRM] = useState<EnrichedContactWithCrm[]>(selectedContacts);
  const [isExportAsLeadAfterReveal, setIsExportAsLeadAfterReveal] = useState(false);

  const isLeadExportEnabled = crmStatusQuery.data?.isLeadExportEnabled;
  const isContactExportEnabled = crmStatusQuery.data?.isContactExportEnabled;
  const isDropdownShown = crm === Crm.salesforce && isLeadExportEnabled && isContactExportEnabled;

  const hasUnrevealedContacts = selectedContacts.some((c) => !c.isRevealed);

  const {
    handleTrackSuccessExport,
    handleTrackFailExport,
    handleTrackSuccessRevealAndExport,
    handleTrackFailRevealAndExport,
  } = useBulkActionsTracking();

  useEffect(() => {
    setEnrichedContactsWithCRM(selectedContacts);
  }, [selectedContacts]);

  const { mutate, isPending } = useMutation({
    mutationFn: exportContactsToCrm,
  });

  const runExport = ({
    asLead,
    revealResult,
    updatedContacts,
    updatedContactsWithCrm,
  }: {
    asLead: boolean;
    revealResult?: RevealBulkResponse;
    updatedContacts?: Contact[];
    updatedContactsWithCrm?: EnrichedContactWithCrm[];
  }) => {
    const payload = asLead
      ? {
          countries: [999],
          leads: (updatedContacts ? updatedContacts : enrichedContactsWithCRM)
            .filter((c) => c.isRevealed)
            .map(({ id, site }) => ({ contactId: id, domain: site })),
        }
      : {
          countries: [999],
          contacts: (updatedContactsWithCrm ? updatedContactsWithCrm : enrichedContactsWithCRM)
            .filter((c) => c.isRevealed)
            .filter((c) => !c.preview?.isExistAsLead)
            .map(({ id, site, crmAccount }) => ({
              contactId: id,
              domain: site,
              accountId: crmAccount?.id === NEW_ACCOUNT_VALUE ? undefined : crmAccount?.id,
            })),
        };

    if (
      (asLead && (!payload.leads || payload.leads.length === 0)) ||
      (!asLead && (!payload.contacts || payload.contacts.length === 0))
    ) {
      return;
    }

    mutate({
      payload,
      crm,
      revealResult,
      onSuccess: () =>
        revealResult ? handleTrackSuccessRevealAndExport() : handleTrackSuccessExport(trackingContextRef.current),
      onError: () =>
        revealResult ? handleTrackFailRevealAndExport() : handleTrackFailExport(trackingContextRef.current),
      onSettled: () => {
        trackingContextRef.current = null;
      },
    });

    appStore.setSelectedContacts([]);
  };
  const handleCrmButtonClick = () => {
    trackingContextRef.current = {
      count: selectedContacts.length,
      isMaster: isMasterCheckboxChecked,
    };
    if (isDropdownShown) {
      setIsDropdownOpened(true);
      return;
    }

    if (crm === Crm.hubspot || (crm === Crm.salesforce && isContactExportEnabled) || hasUnrevealedContacts) {
      setIsPreviewModalOpened(true);
      return;
    }

    if (isLeadExportEnabled) {
      runExport({ asLead: true });
    }
  };

  const handleDropdownLeadClick = () => {
    setIsDropdownOpened(false);

    if (hasUnrevealedContacts) {
      setIsExportAsLeadAfterReveal(true);
      setIsPreviewModalOpened(true);
    } else {
      runExport({ asLead: true });
    }
  };

  const handleDropdownContactClick = () => {
    setIsPreviewModalOpened(true);
    setIsDropdownOpened(false);
  };

  const handleConfirmPreview = ({
    updatedContacts,
    revealResult,
  }: {
    updatedContacts?: EnrichedContactWithCrm[];
    revealResult?: RevealBulkResponse;
  }) => {
    runExport({ asLead: false, revealResult, updatedContactsWithCrm: updatedContacts });
    setIsPreviewModalOpened(false);
    setIsDropdownOpened(false);
  };

  const handleLeadExportAfterReveal = (revealResult?: RevealBulkResponse, updatedContacts?: Contact[]) => {
    runExport({ asLead: true, revealResult, updatedContacts });
    setIsPreviewModalOpened(false);
    setIsDropdownOpened(false);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpened(false);
  };

  return (
    <StyledCrmExportBulkContainer>
      <StyledCrmExportBulkButton
        onClick={handleCrmButtonClick}
        isActive={isDropdownOpened}
        data-automation="CrmExportBulkButton"
        iconName={crm}
        iconSize="xs"
        variant="flat"
        disabled={isDisabled || isPending || (!isLeadExportEnabled && !isContactExportEnabled)}
      >
        {translate('contact_card.export_crm')}
        {isDropdownShown ? <SWReactIcons iconName={isDropdownOpened ? 'chev-up' : 'chev-down'} size="xs" /> : null}
      </StyledCrmExportBulkButton>
      {isDropdownShown ? (
        <Dropdown
          isOpened={isDropdownOpened}
          onClose={() => setIsDropdownOpened(false)}
          placement="left"
          minWidth={120}
        >
          <StyledDropdownOption onClick={handleDropdownContactClick} data-automation="CrmExportDd">
            {translate('contact_card.export_sf.as_contact')}
          </StyledDropdownOption>
          <StyledDropdownOption onClick={handleDropdownLeadClick} data-automation="CrmExportDdOption">
            {translate('contact_card.export_sf.as_lead')}
          </StyledDropdownOption>
        </Dropdown>
      ) : null}
      <BulkContactsExportPreviewModal
        isOpen={isPreviewModalOpened}
        onClose={handleClosePreviewModal}
        crm={crm}
        enrichedContactsWithCRM={enrichedContactsWithCRM}
        setEnrichedContactsWithCRM={setEnrichedContactsWithCRM}
        onConfirm={handleConfirmPreview}
        onExportAsLeadAfterReveal={handleLeadExportAfterReveal}
        isExportAsLeadAfterReveal={isExportAsLeadAfterReveal}
      />
    </StyledCrmExportBulkContainer>
  );
};
